import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";

import { Autocomplete, autocompletePropTypes } from "@swa-ui/core";
import i18n from "@swa-ui/locale";

/**
 * StateAndCountryAutocomplete facilitates selecting a state or country by filtering selectable states based on user input.
 *
 * Note: The application ingesting the StateAndCountryAutocomplete must pass an array of states or countries.
 */

export const StateAndCountryAutocomplete = React.forwardRef((props, ref) => {
  const {
    className,
    defaultValue,
    disabled,
    error,
    id,
    list,
    maxItemsToDisplay,
    noMatchFoundText,
    onBlur,
    onChange,
    required,
    value,
  } = props;

  const inputRef = useRef();
  const processedAutofillValue = useRef();

  useEffect(() => {
    let observer;

    if (onChange && inputRef.current) {
      observer = new MutationObserver(() => {
        const currentValue = inputRef.current?.value;

        if (currentValue && !value && currentValue !== processedAutofillValue.current) {
          processedAutofillValue.current = currentValue;
          const syntheticEvent = {
            currentTarget: inputRef.current,
            target: inputRef.current,
          };

          onChange(syntheticEvent);
        }
      });

      observer.observe(inputRef.current, {
        attributeFilter: ["value"],
        attributes: true,
      });
    }

    return () => observer?.disconnect();
  }, [value, onChange]);

  return <Autocomplete {...getProps()} />;

  function getProps() {
    return {
      "aria-describedby": props["aria-describedby"],
      className,
      defaultValue,
      disabled,
      error,
      id,
      list,
      maxItemsToDisplay,
      noMatchFoundText: noMatchFoundText ?? i18n("StateAndCountryAutocomplete__NO_MATCH_FOUND"),
      onBlur: handleBlur,
      onChange,
      ref: (element) => {
        inputRef.current = element;

        if (typeof ref === "function") {
          ref(element);
        } else if (ref) {
          ref.current = element;
        }
      },
      required,
      value,
    };
  }

  function handleBlur(event) {
    onBlur && onBlur(event);
    onChange && onChange(event);
  }
});

export const stateAndCountryAutocompletePropTypes = {
  /** aria-describedby id to element which provides additional accessibility description of input element. */
  "aria-describedby": PropTypes.string,

  /** Additional class for the component to provide a background color. */
  className: PropTypes.string,

  /**
   * Initial values to select in list. Each value should be separated by a pipe character. For example:
   * "option one|option three".
   */
  defaultValue: PropTypes.string,

  /** Indicates Input should apply disabled styling and ignore mouse and key events. */
  disabled: autocompletePropTypes.disabled,

  /**
   * Indicates Dropdown should apply error styling and apply aria-invalid attribute -- in essence, Input will
   * provide error styling.
   */
  error: autocompletePropTypes.error,

  /** ID to be added to input element. */
  id: autocompletePropTypes.id,

  /** List to be displayed in dropdown. */
  list: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
      })
    ),
  ]),

  /** Total number of items visible. If more items available than can be displayed, they can be scrolled into view. */
  maxItemsToDisplay: autocompletePropTypes.maxItemsToDisplay,

  /** Text to be displayed when no matches are found in the list. */
  noMatchFoundText: PropTypes.string,

  /** Optional event handler to learn when focus is lost from input element. */
  onBlur: autocompletePropTypes.onBlur,

  /** Optional callback to learn of selection changes. */
  onChange: autocompletePropTypes.onChange,

  /** Indicates Input should apply aria-required attribute. */
  required: PropTypes.bool,

  /** Allows the application to control the Input's value. */
  value: PropTypes.string,
};

StateAndCountryAutocomplete.propTypes = stateAndCountryAutocompletePropTypes;

StateAndCountryAutocomplete.defaultProps = {
  list: [],
};
